/**
 * BacklogSchemaBuilder — the shared presentational schema editor consumed by
 * both the project-creation wizard (DRAFT: local backlogFields[] state,
 * nothing hits the network until the whole project form submits — Task 6)
 * and the workspace's Table settings surface (LIVE: every action hits the
 * backlog-field CRUD API immediately — Task 7). Same `.ledger` row list,
 * same FieldEditorModal, same AddColumnControl in both modes — only the
 * wiring differs, isolated behind the mode-discriminated props below.
 */
/* eslint-disable react-refresh/only-export-components --
   this module exports the draftTrio() seed helper alongside the component so
   the project-creation wizard (Task 6) shares one source of truth for the
   draft's initial trio; losing Fast Refresh here is accepted (same precedent
   as FieldBuilderRow/FieldEditorModal/AddColumnControl in this directory). */
import { useState } from 'react';
import { toast } from 'sonner';
import {
  useBacklogFields, useCreateBacklogField, useUpdateBacklogField,
  useReorderBacklogFields, useArchiveBacklogField, useRestoreBacklogField, useDeleteBacklogField,
} from '@/api/backlogField';
import { BACKLOG_FIELD_MANDATORY_TRIO } from '@/types/backlogField';
import type { BacklogField, BacklogSystemField } from '@/types/backlogField';
import { randomUUID } from '@/lib/uuid';
import { theadCellStyle } from '@/components/ProjectForm';
import { FieldBuilderRow, type BuilderField, type FieldBuilderRowTrailingAction } from './FieldBuilderRow';
import { AddColumnControl } from './AddColumnControl';
import { FieldEditorModal, mapBacklogFieldErrorCode, type FieldEditorSubmitValues } from './FieldEditorModal';

function isLocked(systemRef: BacklogSystemField | null): boolean {
  return systemRef !== null && BACKLOG_FIELD_MANDATORY_TRIO.includes(systemRef);
}

function toBuilderField(f: BacklogField): BuilderField {
  return {
    id: f.id, kind: f.kind, systemRef: f.systemRef, label: f.label, dataType: f.dataType,
    options: f.options, required: f.required, archived: f.archived, locked: isLocked(f.systemRef),
  };
}

/** Seeds a DRAFT builder's initial state with the locked trio — every draft
 *  (the create-flow wizard) starts here regardless of what the PM adds. */
export function draftTrio(): BuilderField[] {
  return [
    { id: 'draft-record-id', kind: 'SYSTEM', systemRef: 'RECORD_ID', label: 'Record ID', dataType: null, options: null, required: false, archived: false, locked: true },
    { id: 'draft-date-from', kind: 'SYSTEM', systemRef: 'DATE_FROM', label: 'From', dataType: null, options: null, required: false, archived: false, locked: true },
    { id: 'draft-date-to', kind: 'SYSTEM', systemRef: 'DATE_TO', label: 'To', dataType: null, options: null, required: false, archived: false, locked: true },
  ];
}

function move<T>(arr: T[], from: number, to: number): T[] {
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export interface BacklogSchemaBuilderDraftProps {
  mode: 'draft';
  fields: BuilderField[];
  onChange: (fields: BuilderField[]) => void;
}

export interface BacklogSchemaBuilderLiveProps {
  mode: 'live';
  projectId: string;
}

export type BacklogSchemaBuilderProps = BacklogSchemaBuilderDraftProps | BacklogSchemaBuilderLiveProps;

// Which field (if any) the editor overlay is open for, and in what mode.
type EditorState = { mode: 'create' } | { mode: 'edit'; field: BuilderField } | null;

export function BacklogSchemaBuilder(props: BacklogSchemaBuilderProps) {
  if (props.mode === 'draft') return <DraftBuilder {...props} />;
  return <LiveBuilder {...props} />;
}

// ---------------------------------------------------------------------------
// Shared table shell — active list + collapsed Archived-columns disclosure.
// ---------------------------------------------------------------------------
function BuilderTable({
  active, archived, onMoveUp, onMoveDown, onOpen, trailingActionFor, reorderDisabled, hideReorderFor,
}: {
  active: BuilderField[];
  archived: BuilderField[];
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onOpen: (field: BuilderField) => void;
  trailingActionFor: (field: BuilderField, isArchivedList: boolean) => FieldBuilderRowTrailingAction | undefined;
  reorderDisabled?: boolean;
  /** True hides that active row's reorder cell even though it isn't
   *  archived — DraftBuilder uses this for the locked trio (see Task 6's
   *  note on why a draft-mode trio reorder is a no-op). LiveBuilder omits
   *  this prop, so every active row (trio included) keeps its reorder
   *  arrows there, matching the backend's `/order` contract. */
  hideReorderFor?: (field: BuilderField) => boolean;
}) {
  const [showArchived, setShowArchived] = useState(false);
  return (
    <div>
      <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-md)' }}>
        <table className="ledger">
          <thead>
            <tr>
              <th style={{ ...theadCellStyle, width: 64 }} aria-hidden="true" />
              <th style={theadCellStyle}>Column</th>
              <th style={{ ...theadCellStyle, width: 130 }}>Kind</th>
              <th style={{ ...theadCellStyle, width: 90 }} aria-hidden="true" />
              <th style={{ ...theadCellStyle, width: 56 }} aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {active.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '34px 16px', color: 'var(--text-subtle)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
                  No columns yet.
                </td>
              </tr>
            )}
            {active.map((field, i) => (
              <FieldBuilderRow
                key={field.id}
                field={field}
                index={i}
                count={active.length}
                onMoveUp={() => onMoveUp(field.id)}
                onMoveDown={() => onMoveDown(field.id)}
                onOpen={() => onOpen(field)}
                trailingAction={trailingActionFor(field, false)}
                reorderDisabled={reorderDisabled}
                hideReorder={hideReorderFor?.(field) ?? false}
              />
            ))}
          </tbody>
        </table>
      </div>

      {archived.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            aria-expanded={showArchived}
            className="focus-ring"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 0',
              fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {showArchived ? '▾' : '▸'} Archived columns ({archived.length})
          </button>
          {showArchived && (
            <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-md)', marginTop: 6 }}>
              <table className="ledger">
                <tbody>
                  {archived.map((field, i) => (
                    <FieldBuilderRow
                      key={field.id}
                      field={field}
                      index={i}
                      count={archived.length}
                      onMoveUp={() => {}}
                      onMoveDown={() => {}}
                      onOpen={() => onOpen(field)}
                      trailingAction={trailingActionFor(field, true)}
                      hideReorder
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DRAFT — pure local state, no network. Every mutation is a synchronous
// onChange(next-array) call; the editor's danger zone offers a single
// "Remove" action for every non-locked field (no archive/delete distinction
// — nothing is persisted yet). The locked trio also hides its reorder
// arrows here (hideReorderFor below): submit always strips locked fields
// from the payload and the backend re-injects RECORD_ID first / DATE_FROM
// +DATE_TO last regardless of where they sat in the draft (Task 6), so a
// draft-mode trio reorder would be a silent no-op. LIVE mode keeps trio
// reorder — the backend honors it there.
// ---------------------------------------------------------------------------
function DraftBuilder({ fields, onChange }: BacklogSchemaBuilderDraftProps) {
  const [editor, setEditor] = useState<EditorState>(null);
  const active = fields.filter((f) => !f.archived);
  const archived: BuilderField[] = [];

  function moveUp(id: string) {
    const idx = fields.findIndex((f) => f.id === id);
    if (idx <= 0) return;
    onChange(move(fields, idx, idx - 1));
  }
  function moveDown(id: string) {
    const idx = fields.findIndex((f) => f.id === id);
    if (idx === -1 || idx >= fields.length - 1) return;
    onChange(move(fields, idx, idx + 1));
  }
  function handleAddSystem(ref: BacklogSystemField, defaultLabel: string) {
    onChange([...fields, {
      id: `draft-${ref.toLowerCase()}-${randomUUID()}`, kind: 'SYSTEM', systemRef: ref, label: defaultLabel,
      dataType: null, options: null, required: false, archived: false, locked: false,
    }]);
  }
  function handleSubmit(values: FieldEditorSubmitValues) {
    if (editor?.mode === 'edit') {
      onChange(fields.map((f) => (f.id === editor.field.id ? { ...f, ...values } : f)));
    } else {
      onChange([...fields, {
        id: `draft-custom-${randomUUID()}`, kind: 'CUSTOM', systemRef: null, label: values.label,
        dataType: values.dataType, options: values.options, required: values.required, archived: false, locked: false,
      }]);
    }
    setEditor(null);
  }
  function handleRemove(id: string) {
    onChange(fields.filter((f) => f.id !== id));
    setEditor(null);
  }

  return (
    <div>
      <BuilderTable
        active={active}
        archived={archived}
        onMoveUp={moveUp}
        onMoveDown={moveDown}
        onOpen={(field) => setEditor({ mode: 'edit', field })}
        trailingActionFor={(field) =>
          field.locked
            ? undefined
            : { tooltip: 'Remove', label: `Remove ${field.label}`, icon: 'remove', onClick: () => handleRemove(field.id) }
        }
        hideReorderFor={(field) => field.locked}
      />
      <div style={{ marginTop: 12 }}>
        <AddColumnControl existing={fields} onAddSystem={handleAddSystem} onCreateCustom={() => setEditor({ mode: 'create' })} />
      </div>
      {editor && (
        <FieldEditorModal
          mode={editor.mode}
          field={editor.mode === 'edit' ? editor.field : undefined}
          existingLabels={fields.filter((f) => editor.mode !== 'edit' || f.id !== editor.field.id).map((f) => f.label)}
          onClose={() => setEditor(null)}
          onSubmit={handleSubmit}
          onArchive={editor.mode === 'edit' && !editor.field.locked ? () => handleRemove(editor.field.id) : undefined}
          archiveLabel="Remove"
          archiveDescription="Removes this column from the draft."
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LIVE — every action hits the CRUD API immediately via TanStack Query
// mutations; the query cache (invalidated by every mutation's onSuccess)
// is the single source of truth, so there is no local field-array state.
// Every mutate() call below carries an onError, so a mapped error (e.g.
// INVALID_ORDER on a concurrent reorder) is always surfaced, never
// swallowed. handleSubmit's create/update calls are the one pair that
// route their error through `modalError`/`submitError` instead of
// `reportMutationError` below — the FieldEditorModal is still open at that
// point, so an inline message next to the form beats a toast. Every other
// call here has no such surface to write into (the reorder arrows and
// row-level archive/restore aren't modal actions; the danger-zone archive
// closes the modal synchronously before a response can arrive; the
// danger-zone delete and the palette's add-system create have no error
// slot at all) — those all toast via reportMutationError.
// ---------------------------------------------------------------------------
function reportMutationError(err: unknown) {
  toast.error(mapBacklogFieldErrorCode((err as { code?: string })?.code));
}

function LiveBuilder({ projectId }: BacklogSchemaBuilderLiveProps) {
  const { data: allFields = [], isLoading, isError } = useBacklogFields(projectId, true);
  const createMutation = useCreateBacklogField(projectId);
  const updateMutation = useUpdateBacklogField(projectId);
  const reorderMutation = useReorderBacklogFields(projectId);
  const archiveMutation = useArchiveBacklogField(projectId);
  const restoreMutation = useRestoreBacklogField(projectId);
  const deleteMutation = useDeleteBacklogField(projectId);

  const [editor, setEditor] = useState<EditorState>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const builderFields = allFields.map(toBuilderField);
  const active = builderFields.filter((f) => !f.archived);
  const archived = builderFields.filter((f) => f.archived);

  function moveUp(id: string) {
    const idx = active.findIndex((f) => f.id === id);
    if (idx <= 0) return;
    reorderMutation.mutate(move(active, idx, idx - 1).map((f) => f.id), { onError: reportMutationError });
  }
  function moveDown(id: string) {
    const idx = active.findIndex((f) => f.id === id);
    if (idx === -1 || idx >= active.length - 1) return;
    reorderMutation.mutate(move(active, idx, idx + 1).map((f) => f.id), { onError: reportMutationError });
  }

  function handleSubmit(values: FieldEditorSubmitValues) {
    setModalError(null);
    const onError = (err: unknown) => setModalError(mapBacklogFieldErrorCode((err as { code?: string })?.code));
    if (editor?.mode === 'edit') {
      updateMutation.mutate(
        { id: editor.field.id, payload: { label: values.label, options: values.options, required: values.required } },
        { onSuccess: () => setEditor(null), onError },
      );
    } else {
      createMutation.mutate(
        { projectId, kind: 'CUSTOM', label: values.label, dataType: values.dataType, options: values.options, required: values.required },
        { onSuccess: () => setEditor(null), onError },
      );
    }
  }

  function handleAddSystem(ref: BacklogSystemField, defaultLabel: string) {
    createMutation.mutate(
      { projectId, kind: 'SYSTEM', systemRef: ref, label: defaultLabel, required: false },
      { onError: reportMutationError },
    );
  }

  function closeEditor() {
    setEditor(null);
    setModalError(null);
  }

  return (
    <div>
      {isLoading && <div className="skeleton" style={{ height: 160, borderRadius: 'var(--radius-md)' }} aria-label="Loading backlog schema" />}
      {isError && (
        <div role="alert" style={{ padding: '12px 16px', background: 'var(--error-bg)', borderLeft: '3px solid var(--error)', borderRadius: 'var(--radius-md)', color: 'var(--error)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          Failed to load the backlog schema.
        </div>
      )}
      {!isLoading && !isError && (
        <>
          <BuilderTable
            active={active}
            archived={archived}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
            onOpen={(field) => setEditor({ mode: 'edit', field })}
            trailingActionFor={(field, isArchivedList) => {
              if (field.locked) return undefined;
              if (isArchivedList) {
                return { tooltip: 'Restore', label: `Restore ${field.label}`, icon: 'restore', onClick: () => restoreMutation.mutate(field.id, { onError: reportMutationError }) };
              }
              return { tooltip: 'Archive', label: `Archive ${field.label}`, icon: 'archive', onClick: () => archiveMutation.mutate(field.id, { onError: reportMutationError }) };
            }}
            reorderDisabled={reorderMutation.isPending}
          />
          <div style={{ marginTop: 12 }}>
            <AddColumnControl
              existing={builderFields}
              onAddSystem={handleAddSystem}
              onCreateCustom={() => setEditor({ mode: 'create' })}
              disabled={createMutation.isPending}
            />
          </div>
        </>
      )}
      {editor && (
        <FieldEditorModal
          mode={editor.mode}
          field={editor.mode === 'edit' ? editor.field : undefined}
          existingLabels={builderFields.filter((f) => editor.mode !== 'edit' || f.id !== editor.field.id).map((f) => f.label)}
          onClose={closeEditor}
          onSubmit={handleSubmit}
          onArchive={editor.mode === 'edit' && !editor.field.locked ? () => { archiveMutation.mutate(editor.field.id, { onError: reportMutationError }); setEditor(null); } : undefined}
          onDelete={editor.mode === 'edit' && editor.field.kind === 'CUSTOM' ? () => deleteMutation.mutate(editor.field.id, { onSuccess: () => setEditor(null), onError: reportMutationError }) : undefined}
          isPending={createMutation.isPending || updateMutation.isPending}
          submitError={modalError}
        />
      )}
    </div>
  );
}
